import { captureException } from "@sentry/nextjs";
import { FunctionComponent, useEffect, useRef } from "react";

import {
  GQLPostTemplateEnum,
  GQLResourceTypeEnum,
} from "@framework/__generated__/graphql/schema";
import { pageLoadUUID, initConciergePlugins } from "@framework/ads/ads.utils";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";

import { applyMobileAppArticleDfpVariables } from "./mobile-app-article-dfp";

export {
  applyMobileAppArticleDfpVariables,
  MOBILE_APP_ARTICLE_PAGE_TYPE,
} from "./mobile-app-article-dfp";

declare global {
  interface Window {
    dfpVariables?: DFPVariables;
    dfpVariablesBoostArticle?: Partial<DFPVariables>;
    CONCERT_ADS_CONFIG?: object;
    concertAdsInstalled?: boolean;
    concertAds?: ConcertAdsInterface;
    ConcertAds?: {
      new ({
        cmd,
        slug,
        dfpVariables,
        isPreview,
        excludedContainers,
      }: {
        cmd: string[];
        slug: string;
        dfpVariables: DFPVariables;
        isPreview: boolean;
        settings?: object;
        excludedContainers?: string[];
      }): ConcertAdsInterface;
    };
  }
}

interface ConcertAdsInterface {
  loadRemoteConfig: (configUrl: string) => Promise<Response>;
  getAllVariables: () => DFPVariables;
  install: () => void;
  reinstall: (
    targetElement: Element | Document,
    updatedSettings: object,
    slotTargeting: Record<string, any>,
    shouldUninstall?: boolean,
    preserveTargeting?: boolean,
    destroySlotsMarkedForDestroy?: boolean,
  ) => void;
  insertSlots: () => void;
  settings: object;
}

interface DFPVariables {
  entitlements?: string[];
  hostname: string;
  test_hostname: string;
  author?: string[];
  branded_content?: true;
  keywords: string[];
  page_type: string[]; // TODO (rev-exp): POST-LAUNCH: remove this after launch
  entry_blurb?: string[];
  entry_slug?: string[];
  entry_type?: string[];
  entry_title?: string[];
  entry_id?: (number | string)[];
  entry_published_date?: string[];
  entry_group?: string[];
  package_id?: string[];
  hub_page?: string[];
  login?: boolean;
  network: string[];
  affiliation: string[];
  team?: string;
  pageload_id: string | null;
  vox_creative_advertiser?: string;
  vox_creative_campaign?: string;
  boost?: boolean;
  boosted_entry_id?: (number | string)[];
  boosted_entry_group?: string[];
  /** Present only for authenticated mobile-app WebView article traffic. */
  in_app?: boolean;
}

interface Props {
  preview?: boolean;
  title?: string;
  dek?: string;
  seoHeadline?: string | null;
  globalId?: string;
  chorusId?: number | null;
  packageIds?: string[];
  permalink?: string | null;
  campaign?: {
    title: string;
    advertiserName: string;
  } | null;
  pageType?:
    | "home_page"
    | "feed_page"
    | "hub_page"
    | "interior_page"
    | "author_page"
    | "archive_page"
    | "user_profiles";
  authors?: string[] | null;
  originalPublishedAt?: string | null;
  categories?: string[] | null;
  hubPages?: string[] | null;
  affiliation?: string[];
  adSlugParts?: string[];
  template?: GQLPostTemplateEnum | null;
  resourceType?: GQLResourceTypeEnum | null;
  boostedPostEnabled?: boolean;
  boostedPostGlobalId?: string;
  boostedPostChorusId?: number | null;
  boostedPostCategories?: string[];
  excludedContainers?: string[];
  isMobileAppRequest?: boolean;
}

function getAdSlugPrefix({
  gam,
  concertAdsNetwork,
  communityDomain,
  league = "general",
  getAdNetworkPath,
}: {
  gam: string;
  concertAdsNetwork: string;
  communityDomain: string;
  league?: string;
  getAdNetworkPath?: (league: string) => string;
}): string {
  const domainParts = communityDomain.split(".");
  const rootDomain = domainParts.slice(domainParts.length - 2).join(".");
  const network = getAdNetworkPath
    ? getAdNetworkPath(league)
    : concertAdsNetwork;
  // Note: (cvle) Ads team wants to overhaul the whole slug structure, so this is likely to change.
  return `/${gam}/${network}/${rootDomain}`;
}

/**
 * If a league or sport is available, use an array of both for the affiliation
 * otherwise use the default value
 */
function getFinalAffiliation({
  affiliation,
  sport,
  league,
}: {
  affiliation: string[];
  sport?: string;
  league?: string;
}) {
  const finalAffiliation =
    sport || league
      ? [sport, league].filter((item): item is string => Boolean(item))
      : affiliation;

  return [...new Set(finalAffiliation)];
}

/**
 * Determines the Concert Ads Config url
 */
export function getConcertAdsConfig({
  concertAdsNetwork,
  communityId,
  getCommunityAdConfigPath,
}: {
  concertAdsNetwork: string;
  communityId: number;
  getCommunityAdConfigPath?: (communityId: number) => string;
}) {
  const adConfigPath: string = getCommunityAdConfigPath
    ? getCommunityAdConfigPath(communityId)
    : concertAdsNetwork;

  return `https://concertads-configs.vox-cdn.com/duet/${adConfigPath}/config.json`;
}

const ConcertAds: FunctionComponent<Props> = (props) => {
  const {
    networkConfig: {
      concertAds: { gam, network },
    },
    communityConfig: { communityDomain, communityId },
    adConfig: { communityAdConfig, networkAdConfig },
  } = useConfigContext();

  const getCommunityAdConfigPath = communityAdConfig?.getCommunityAdConfigPath;
  const getCommunitySportsAdConfig =
    communityAdConfig?.getCommunitySportsAdConfig;
  // networkAdConfig is optional; callback type is validated at app config layer
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const getAdNetworkPath = networkAdConfig?.getAdNetworkPath;

  const { sport, league, team } = getCommunitySportsAdConfig
    ? getCommunitySportsAdConfig(communityId)
    : { sport: undefined, league: undefined, team: undefined };

  const auth = useAuth();

  const concertAdsConfig = getConcertAdsConfig({
    concertAdsNetwork: network,
    communityId,
    getCommunityAdConfigPath,
  });

  const BOOSTED_POST_WRAPPER_SELECTOR = ".duet--article--boosted-post-wrapper";

  useEffect(() => {
    if (auth.userLoading) {
      return;
    }

    const install = () => {
      if (window.concertAdsInstalled) {
        console.log(
          "We are not installing Concert Ads because it has already been installed.",
        );
        return;
      }

      window.concertAdsInstalled = true;

      if (!window.ConcertAds) {
        captureException(
          "ConcertAds instance is not available on the window to install",
        );
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const concertConfigUrlParam = urlParams.get("concert_config_url");
      const configUrl = concertConfigUrlParam
        ? concertConfigUrlParam
        : concertAdsConfig;

      const {
        title,
        dek = null,
        seoHeadline = null,
        chorusId = null,
        globalId = null,
        pageType = "interior_page",
        permalink = null,
        authors = null,
        originalPublishedAt = null,
        categories = null,
        hubPages = null,
        affiliation = ["general"],
        adSlugParts = [],
        template = null,
        resourceType = null,
        campaign = null,
        packageIds = [],
        isMobileAppRequest,
      } = props;

      // For standalone pages we only load ads on certain pages
      if (resourceType === "page" && !pageIncludesAds(title)) {
        return;
      }

      const keywords: string[] = [
        ...new Set([
          ...(seoHeadline || title || "")
            .replace(/[^a-z]/gi, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase()
            .split(" "),
          ...(categories || []),
        ]),
      ];

      const isLoggedIn = Boolean(auth.user);

      const finalAffiliation = getFinalAffiliation({
        affiliation,
        sport,
        league,
      });

      const dfpVariables: DFPVariables = {
        page_type: [pageType],
        keywords,
        affiliation: finalAffiliation,
        network: [network],
        pageload_id: pageLoadUUID(),
        hostname: location.hostname,
        test_hostname: location.hostname.includes("duet.voxops.net")
          ? "duet.voxops.net"
          : location.hostname,
      };

      applyMobileAppArticleDfpVariables(dfpVariables, {
        isMobileAppRequest,
        pageType,
      });

      if (auth.user?.entitlements && auth.user.entitlements.length > 0) {
        dfpVariables.entitlements = auth.user.entitlements;
      }

      if (isLoggedIn) {
        dfpVariables.login = isLoggedIn;
      }

      if (team) {
        dfpVariables.team = team;
      }

      if (permalink) {
        dfpVariables.entry_slug = [new URL(permalink).pathname.substring(1)];
      }

      if (chorusId || globalId) {
        dfpVariables.entry_id = [];
        if (chorusId) {
          dfpVariables.entry_id.push(chorusId);
        }
        if (globalId) {
          dfpVariables.entry_id.push(globalId);
        }
      }

      if (authors && authors.length > 0) {
        dfpVariables.author = authors;
      }

      if (originalPublishedAt) {
        // Concert/conductor expects this to be an array of date strings
        dfpVariables.entry_published_date = [originalPublishedAt];
      }

      if (categories && categories.length > 0) {
        dfpVariables.entry_group = categories;
      }

      if (hubPages && hubPages.length > 0) {
        dfpVariables.hub_page = hubPages;
      }

      if (resourceType) {
        if (categories && categories.includes("reviews")) {
          dfpVariables.entry_type = ["review"];
        } else if (resourceType === "post") {
          dfpVariables.entry_type = [template === null ? "article" : "feature"];
        } else {
          dfpVariables.entry_type = [resourceType.toLowerCase()];
        }
      }

      if (packageIds.length > 0) {
        dfpVariables.package_id = packageIds;
      }

      if (campaign) {
        dfpVariables.branded_content = true;
        dfpVariables.vox_creative_advertiser = campaign.advertiserName;
        dfpVariables.vox_creative_campaign = campaign.title;
      }

      if (title) {
        dfpVariables.entry_title = [title];
      }
      if (dek) {
        dfpVariables.entry_blurb = [dek];
      }

      // This timeout is how long we keep trying to install the Concierge
      // until the Concierge script loads
      const conciergeScriptLoadTimeout = Date.now() + 20000;
      initConciergePlugins(conciergeScriptLoadTimeout);

      const previewAds =
        urlParams.get("concert_preview") !== null || Boolean(props.preview);

      const excludedContainers: string[] = [
        ...(props.excludedContainers ?? []),
      ];
      if (document.querySelector(BOOSTED_POST_WRAPPER_SELECTOR)) {
        excludedContainers.push(BOOSTED_POST_WRAPPER_SELECTOR);
      }

      const args = {
        cmd: [],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- getAdNetworkPath from networkAdConfig (optional)
        slug: `${getAdSlugPrefix({ gam, concertAdsNetwork: network, communityDomain, league, getAdNetworkPath })}/${adSlugParts.join("/")}`,
        isPreview: previewAds,
        dfpVariables,
        hasSubscription: auth.user?.isEntitled,
        loggedIn: Boolean(auth.user),
        excludedContainers:
          excludedContainers.length > 0 ? excludedContainers : undefined,
      };

      const concertAds = new window.ConcertAds(args);
      window.concertAds = concertAds;
      concertAds
        .loadRemoteConfig(configUrl)
        .then(() => {
          concertAds.install();
          window.CONCERT_ADS_CONFIG = concertAds.settings;
          window.dfpVariables = concertAds.getAllVariables();
          // Dispatch event so boost ads effect can re-check prerequisites
          window.dispatchEvent(new CustomEvent("dfpVariablesReady"));
        })
        .catch((err) =>
          captureException(err, {
            extra: {
              message:
                "There was an error loading the ConcertAds remote config",
            },
          }),
        );
    };

    if (!window.ConcertAds) {
      window.addEventListener("concertAdsReady", install);

      return () => {
        window.removeEventListener("concertAdsReady", install);
      };
    } else {
      install();
    }
    // Only keep `userLoading` as a dependency here, so that it will only rerun once after
    // the user info has been loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.userLoading]);

  // Listen for boosted post data changes and update DFP variables
  const {
    boostedPostEnabled = false,
    boostedPostGlobalId = null,
    boostedPostChorusId = null,
    boostedPostCategories = [],
  } = props;

  const boostedAdsInstalledRef = useRef(false);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    // Disconnect any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // If already installed, don't do anything
    if (boostedAdsInstalledRef.current) {
      return;
    }

    if (!boostedPostEnabled) {
      return;
    }

    /**
     * Install boost ads when both prerequisites and target element exist
     */
    const installBoostAds = (targetElement: Element) => {
      if (boostedAdsInstalledRef.current) {
        return;
      }

      // Verify prerequisites are still met
      if (
        !window.concertAdsInstalled ||
        !window.concertAds ||
        !window.dfpVariables
      ) {
        return;
      }

      const slotTargeting: Record<string, any> = {
        boost: ["true"],
      };

      const boostedEntryId = [boostedPostChorusId, boostedPostGlobalId]
        .filter(Boolean)
        .map(String);
      if (boostedEntryId.length > 0) {
        slotTargeting.boost_entry_id = boostedEntryId;
      }

      if (boostedPostCategories.length > 0) {
        slotTargeting.boost_entry_group = boostedPostCategories;
      }

      // Install boosted ads in scoped container
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window.concertAds) {
        window.concertAds.reinstall(
          targetElement, // Only query within this DOM element
          {}, // updatedSettings - no settings to update
          slotTargeting, // Targeting to apply to new slots only
          false, // uninstall = false (KEEP existing page ads)
          true, // preserveTargeting = true (don't reset page-level targeting)
          true, // destroySlotsMarkedForDestroy = true (destroy slots marked for destruction)
        );
      }

      boostedAdsInstalledRef.current = true;
    };

    // Prerequisites are ready - check if element exists or set up observer
    const setupObserver = () => {
      // Check if element already exists
      const existingElement = document.querySelector(
        BOOSTED_POST_WRAPPER_SELECTOR,
      );
      if (existingElement) {
        installBoostAds(existingElement);
        return;
      }

      // Set up MutationObserver to watch for when the element is added to the DOM
      observerRef.current = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          const addedNodes = Array.from(mutation.addedNodes);
          for (const addedNode of addedNodes) {
            if (addedNode.nodeType === Node.ELEMENT_NODE) {
              const element = addedNode as Element;
              // Check if the added node itself matches the selector
              if (element.matches(BOOSTED_POST_WRAPPER_SELECTOR)) {
                installBoostAds(element);
                observerRef.current?.disconnect();
                observerRef.current = null;
                return;
              }
              // Check if any descendant matches the selector
              const matchingDescendant = element.querySelector(
                BOOSTED_POST_WRAPPER_SELECTOR,
              );
              if (matchingDescendant) {
                installBoostAds(matchingDescendant);
                observerRef.current?.disconnect();
                observerRef.current = null;
                return;
              }
            }
          }
        }
      });

      // Start observing the document body for added nodes
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });
    };

    // Always set up observer to watch for element (prerequisites checked inside installBoostAds)
    setupObserver();

    // Also listen for when dfpVariables becomes available (in case element appears before prerequisites are ready)
    const handleDfpVariablesReady = () => {
      const existingElement = document.querySelector(
        BOOSTED_POST_WRAPPER_SELECTOR,
      );
      if (existingElement) {
        installBoostAds(existingElement);
      }
    };

    window.addEventListener("dfpVariablesReady", handleDfpVariablesReady);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      window.removeEventListener("dfpVariablesReady", handleDfpVariablesReady);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boostedPostEnabled,
    boostedPostGlobalId,
    boostedPostChorusId,
    boostedPostCategories,
  ]);

  return null;
};

export default withGracefulErrorBoundary(ConcertAds, {
  context: "ConcertAdsErrorBoundary",
});

/**
 * We only load ConcertAds on the following standalone pages
 * @param title
 * @returns boolean
 */
function pageIncludesAds(title: string | undefined) {
  const pagesWithAds = ["how to pitch", "masthead"];

  return pagesWithAds.some(
    (titleWithAds) => title && title.toLowerCase().includes(titleWithAds),
  );
}
